import { TriangleDownIcon } from "@chakra-ui/icons";
import { Box, chakra, Flex, Grid, Text } from "@chakra-ui/react";
import { blue, fonts, palette } from "@intentsify/brand";
import {
  createChartScene,
  defineChart,
  renderChartSvg,
} from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { useMemo, useState } from "react";
import { useMeasure } from "react-use";
import { byLook } from "theme/look";
import { BrandChart, BrandChartTheme, useBrandChartTheme } from "../BrandChart";
import { EXPORT_TITLE_HEIGHT } from "../Charts.const";
import { measureTextWidth, trimTextByWidth } from "../Charts.utils";
import { LegacyTornadoChart } from "./legacy/TornadoChart";
import {
  buildTornadoSideDefinition,
  getTornadoRows,
} from "./TornadoChart.definition";
import {
  Side,
  TornadoChartDataSide,
  TornadoChartItem,
  TornadoChartProps,
} from "./TornadoChart.types";

export type {
  TornadoChartData,
  TornadoChartLabels,
} from "./TornadoChart.types";

const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 36;
const LABEL_FONT_SIZE = 14;
const LABEL_WIDTH_MIN = 120;
const LABEL_WIDTH_MAX = 300;
const LABEL_GAP = 16;

const sideFill = (brandTheme: BrandChartTheme): Record<Side, string> => ({
  left: brandTheme.mode === "light" ? palette.blue : blue[300],
  right: brandTheme.primary,
});

const useLabelWidth = (
  left: TornadoChartDataSide,
  right: TornadoChartDataSide,
) =>
  useMemo(() => {
    const widest = Math.max(
      0,
      ...[...left.items, ...right.items].map(({ name }) =>
        measureTextWidth(name, { fontSize: LABEL_FONT_SIZE }),
      ),
    );

    return Math.min(
      Math.max(widest + LABEL_GAP, LABEL_WIDTH_MIN),
      LABEL_WIDTH_MAX,
    );
  }, [left, right]);

/** Static single-SVG render for PNG export (see useExportableChart). */
const TornadoExportSvg = ({
  id,
  title,
  labels,
  rows,
  sides,
  sortSide,
  labelWidth,
  brandTheme,
}: {
  id?: string;
  title?: string;
  labels: [string, string];
  rows: TornadoChartItem[];
  sides: [TornadoChartDataSide, TornadoChartDataSide];
  sortSide: Side;
  labelWidth: number;
  brandTheme: BrandChartTheme;
}) => {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const fills = sideFill(brandTheme);
  const foreground = brandTheme.theme.foreground ?? palette.ink;
  const titleHeight = title ? EXPORT_TITLE_HEIGHT : 0;
  const rowsHeight = ROW_HEIGHT * rows.length;
  const height = titleHeight + HEADER_HEIGHT + rowsHeight;
  const sideWidth = Math.max(0, (width - labelWidth) / 2);

  const halves = useMemo(() => {
    if (sideWidth <= 0) {
      return null;
    }

    const size = { width: sideWidth, height: rowsHeight };

    return (["left", "right"] as const).map((side, index) =>
      renderChartSvg(
        createChartScene(
          buildTornadoSideDefinition({
            rows,
            side,
            maxReferenceScore: sides[index].maxReferenceScore,
            brandTheme,
            fill: fills[side],
          }),
          size,
        ),
        { ariaLabel: labels[index], idPrefix: `${id ?? "tornado"}-${side}` },
      ).replace(
        'width="100%" height="100%"',
        `width="${size.width}" height="${size.height}"`,
      ),
    );
  }, [brandTheme, fills, id, labels, rows, rowsHeight, sideWidth, sides]);

  const headerY = titleHeight + HEADER_HEIGHT / 2;

  return (
    <div ref={ref} style={{ width: "100%" }}>
      {halves && (
        <svg
          id={id}
          width={width}
          height={height}
          style={{ fontFamily: fonts.stack, color: foreground }}
        >
          {title && (
            <text
              x={22}
              y={30}
              dominantBaseline="middle"
              fontSize={22}
              fontWeight={500}
              fill={foreground}
            >
              {title}
            </text>
          )}
          {(["left", "right"] as const).map((side, index) => (
            <text
              key={side}
              x={side === "left" ? sideWidth / 2 : width - sideWidth / 2}
              y={headerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={14}
              fontWeight={600}
              fill={foreground}
            >
              {labels[index]}
              {sortSide === side ? " ▾" : ""}
            </text>
          ))}
          <g
            transform={`translate(0,${titleHeight + HEADER_HEIGHT})`}
            dangerouslySetInnerHTML={{ __html: halves[0] }}
          />
          <g
            transform={`translate(${sideWidth + labelWidth},${
              titleHeight + HEADER_HEIGHT
            })`}
            dangerouslySetInnerHTML={{ __html: halves[1] }}
          />
          {rows.map((row, index) => (
            <text
              key={row.name}
              x={sideWidth + labelWidth / 2}
              y={titleHeight + HEADER_HEIGHT + ROW_HEIGHT * (index + 0.5)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={LABEL_FONT_SIZE}
              fill={foreground}
            >
              {trimTextByWidth(row.name, {
                fontSize: LABEL_FONT_SIZE,
                maxWidth: labelWidth - LABEL_GAP,
              })}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
};

const SortHeader = ({
  label,
  isSorted,
  color,
  onClick,
}: {
  label: string;
  isSorted: boolean;
  color: string;
  onClick: () => void;
}) => (
  <chakra.button
    type="button"
    onClick={onClick}
    aria-pressed={isSorted}
    title={`Sort by ${label}`}
    display="flex"
    alignItems="center"
    justifyContent="center"
    gap={1.5}
    height={`${HEADER_HEIGHT}px`}
    borderBottom="2px solid"
    borderColor={isSorted ? color : "transparent"}
    fontSize="sm"
    fontWeight="semibold"
    _hover={{ borderColor: color }}
  >
    <Box as="span" w={2.5} h={2.5} borderRadius="full" bg={color} />
    {label}
    {isSorted && (
      <TriangleDownIcon boxSize={2.5} aria-label="sorted descending" />
    )}
  </chakra.button>
);

const BrandTornadoChart = ({
  data,
  labels,
  title,
  minHeight,
  id,
  defaultSortSide = "left",
  tooltipRenderer,
  exportMode = false,
}: TornadoChartProps) => {
  const [leftSide, rightSide] = data;
  const brandTheme = useBrandChartTheme();
  const fills = sideFill(brandTheme);
  const [sortSide, setSortSide] = useState<Side>(defaultSortSide);
  const rows = useMemo(() => getTornadoRows(data, sortSide), [data, sortSide]);
  const labelWidth = useLabelWidth(leftSide, rightSide);

  const definitions = useMemo(
    () =>
      (["left", "right"] as const).map((side) => {
        const sideData = side === "left" ? leftSide : rightSide;

        const chart = buildTornadoSideDefinition({
          rows,
          side,
          maxReferenceScore: sideData.maxReferenceScore,
          brandTheme,
          fill: fills[side],
        });

        // Interaction options belong to the responsive config.
        return defineChart({ chart: () => chart, tooltip: { use: tooltip } });
      }),
    // `fills` derives from brandTheme.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, leftSide, rightSide, brandTheme],
  );

  if (exportMode) {
    return (
      <TornadoExportSvg
        id={id}
        title={title}
        labels={labels}
        rows={rows}
        sides={data}
        sortSide={sortSide}
        labelWidth={labelWidth}
        brandTheme={brandTheme}
      />
    );
  }

  const rowsHeight = `${ROW_HEIGHT * rows.length}px`;

  return (
    <Flex
      direction="column"
      width="100%"
      minHeight={minHeight}
      overflowY="auto"
    >
      <Grid templateColumns={`1fr ${labelWidth}px 1fr`} alignItems="end">
        <SortHeader
          label={labels[0]}
          isSorted={sortSide === "left"}
          color={fills.left}
          onClick={() => setSortSide("left")}
        />
        <Box />
        <SortHeader
          label={labels[1]}
          isSorted={sortSide === "right"}
          color={fills.right}
          onClick={() => setSortSide("right")}
        />
      </Grid>

      <Grid templateColumns={`1fr ${labelWidth}px 1fr`} height={rowsHeight}>
        {(["left", "right"] as const).map((side, index) => (
          <Box
            key={side}
            gridColumn={side === "left" ? 1 : 3}
            gridRow={1}
            height={rowsHeight}
          >
            <BrandChart
              definition={definitions[index]}
              brandTheme={brandTheme}
              ariaLabel={labels[index]}
              height={rowsHeight}
              renderTooltipBody={({ points }) => {
                const row = points[0]?.datum;

                return row
                  ? tooltipRenderer({ sortSide, hoverSide: side, name: row.name })
                  : null;
              }}
            />
          </Box>
        ))}

        <Flex gridColumn={2} gridRow={1} direction="column">
          {rows.map((row) => (
            <Flex
              key={row.name}
              height={`${ROW_HEIGHT}px`}
              alignItems="center"
              justifyContent="center"
              px={2}
            >
              <Text fontSize="sm" noOfLines={1} title={row.name}>
                {row.name}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Grid>
    </Flex>
  );
};

export const TornadoChart = byLook(BrandTornadoChart, LegacyTornadoChart);
